import { debounce, pluralizeWord } from 'application/utils/helpers'
import { CategoriesData } from './_data'

const PER_PAGE = 16

/**
 * Управляет отображением, поиском, сортировкой и пагинацией списка категорий.
 */
export class CategoriesPage {
  private data: CategoriesData
  private loadMoreButton: HTMLElement
  private categoriesList: HTMLElement
  private header: HTMLElement
  private currentPage: number
  private totalPages: number
  private sortValue: string = 'default'
  private searchElement: HTMLInputElement
  private categories: HTMLElement[]
  private notFoundMessage: HTMLElement | null = null

  /**
   * Инициализирует страницу категорий и связывает DOM-элементы с логикой.
   */
  constructor() {
    this.loadMoreButton = document.querySelector('[data-load-more]')!
    this.categoriesList = document.querySelector('[data-categories-list]')!
    this.searchElement = document.querySelector('[data-categories-search]')!
    this.header = document.querySelector('[data-categories-header]')!
    this.notFoundMessage = document.querySelector('[data-not-found]')
    this.currentPage = 1
    this.data = new CategoriesData(this.categoriesList)
    this.categories = this.data.getCategories()
    this.init()
  }

  /**
   * Подписывает обработчики событий и запускает первичный рендер списка.
   */
  private init() {
    this.loadMoreButton.addEventListener('click', () => this.loadMore())
    this.updateCategoriesVisibility()
    this.updateLoadMoreButtonVisibility()

    const sortElement = document.querySelector(
      '[data-dropdown-name="categories-sort"]'
    )

    sortElement?.addEventListener('dropdown-change', (event) =>
      this.onSortChange(event as CustomEvent)
    )

    const defaultSortOption = sortElement?.querySelector<HTMLElement>(
      '.dropdown__option_active'
    )

    if (defaultSortOption) {
      this.sortValue = defaultSortOption.dataset.dropdownValue || 'default'
      this.updateSort(this.sortValue)
    }

    this.searchElement.addEventListener(
      'input',
      debounce(() => this.onSearchInput(), 600)
    )

    const searchResetButton = document.querySelector(
      '[data-search-clear]'
    ) as HTMLElement
    this.searchElement.addEventListener('input', () => {
      searchResetButton.classList.toggle(
        'is-visible',
        this.searchElement.value !== ''
      )
    })

    searchResetButton.addEventListener('click', this.onSearchReset)
  }

  /**
   * Обрабатывает ввод в поле поиска и сбрасывает пагинацию.
   */
  private onSearchInput() {
    this.currentPage = 1
    this.updateCategoriesVisibility()
    this.updateLoadMoreButtonVisibility()
  }

  /**
   * Сбрасывает строку поиска и возвращает исходное состояние списка.
   * @param {Event} event Событие клика по кнопке очистки поля поиска.
   */
  private onSearchReset = (event: Event) => {
    const target = (event.target as HTMLElement).closest(
      '[data-search-clear]'
    ) as HTMLElement
    if (!target) return

    this.searchElement.value = ''
    this.currentPage = 1
    this.updateCategoriesVisibility()
    this.updateLoadMoreButtonVisibility()
    target.classList.remove('is-visible')
  }

  /**
   * Вычисляет общее количество страниц с учётом текущего массива категорий.
   * @return {number} Количество страниц.
   */
  private calcTotalPages(): number {
    return Math.ceil(this.categories.length / PER_PAGE)
  }

  /**
   * Загружает следующую страницу категорий, если она существует.
   */
  private loadMore() {
    if (this.currentPage < this.totalPages) {
      this.currentPage++
      this.updateCategoriesVisibility()
      this.updateLoadMoreButtonVisibility()
    }
  }

  /**
   * Обрабатывает изменение выбранной сортировки из компонента dropdown.
   * @param {CustomEvent} event Кастомное событие с новым значением сортировки.
   */
  private onSortChange(event: CustomEvent) {
    const { value } = event.detail
    this.updateSort(value)
  }

  /**
   * Применяет новое значение сортировки и перерисовывает список.
   * @param {string} value Значение сортировки.
   */
  private updateSort(value: string) {
    this.sortValue = value
    this.currentPage = 1
    this.updateCategoriesVisibility()
    this.updateLoadMoreButtonVisibility()
  }

  /**
   * Фильтрует, сортирует и отображает категории с учётом текущего состояния страницы.
   */
  private updateCategoriesVisibility() {
    const searchValue = this.searchElement.value.toLowerCase()

    this.categories = this.data.getFilteredCategories(searchValue)

    if (this.sortValue === 'new') {
      this.categories.sort((a, b) => {
        const aNew = a.dataset.new === 'true' ? 1 : 0
        const bNew = b.dataset.new === 'true' ? 1 : 0
        return bNew - aNew
      })
    } else if (this.sortValue === 'recommended') {
      this.categories.sort((a, b) => {
        const aRec = a.dataset.recommended === 'true' ? 1 : 0
        const bRec = b.dataset.recommended === 'true' ? 1 : 0
        return bRec - aRec
      })
    }

    this.categories.forEach((category, index) => {
      category.classList.toggle(
        'is-hidden',
        index >= this.currentPage * PER_PAGE
      )
    })

    this.categoriesList.innerHTML = ''
    this.categories.forEach((category) =>
      this.categoriesList.appendChild(category)
    )

    this.updateCategoriesCount(this.categories.length)
    this.header.classList.toggle('is-hidden', this.categories.length === 0)
    this.notFoundMessage?.classList.toggle(
      'is-hidden',
      this.categories.length > 0
    )

    this.totalPages = this.calcTotalPages()
  }

  /**
   * Обновляет видимость кнопки подгрузки в зависимости от текущей страницы.
   */
  private updateLoadMoreButtonVisibility() {
    this.totalPages = this.calcTotalPages()

    this.loadMoreButton.classList.toggle(
      'is-hidden',
      this.currentPage >= this.totalPages
    )
  }

  /**
   * Обновляет текст счётчика найденных категорий в заголовке.
   * @param {number} count Количество категорий после фильтрации.
   */
  private updateCategoriesCount(count: number) {
    const countElement = document.querySelector('[data-categories-count]')
    if (countElement) {
      countElement.textContent = `${count} ${pluralizeWord(count, ['категория', 'категории', 'категорий'])}`
    }
  }
}
